/**
 * messageContext — shared inbox + active conversation message state.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type PropsWithChildren,
} from 'react';
import messageService, {
  type ConversationPrefsMap,
} from '../api/services/messageService';
import { isLocalMessageId } from '../api/conversation/conversationMapper';
import { isModerationHoldError } from '../api/moderation/moderationErrors';
import { STORAGE_KEYS } from '../constants';
import {
  CONVERSATIONS_PAGE_SIZE,
  isAiConversationType,
  isGroupConversationType,
} from '../constants/conversation';
import { useAuth } from './AuthContext';
import { useProfile } from './ProfileContext';
import i18n from '../i18n';
import { useTranslation } from '../i18n/useTranslation';
import { formatContentTimestamp } from '../utils/dateHelpers';
import { storage } from '../platform/storage';
import { eventBus } from '../platform/eventBus';
import { USER_SESSION_CLEARED_EVENT } from '../api/auth/clearUserSession';
import { getUserFacingErrorMessage } from '../utils/apiErrorHandler';
import {
  normalizeConversation,
  sortConversations,
} from '../utils/sortConversations';
import {
  applyUnreadToConversation,
  countUnreadConversations,
  mergeUnreadByConversationId,
  sumUnreadByConversationId,
  shouldCountConversationUnread,
  conversationUnreadFingerprint,
  toBackendMessageId,
} from '../utils/unreadMessages';
import { needsGroupCollageHydration, hasUsableGroupAvatar } from '../utils/groupConversation';
import {
  buildReplyToFromMessage,
  mergeReplyTo,
} from '../utils/chatReplyPreview';
import { resolveReplyAfterConversationSelection } from '../utils/chatReplySelection';
import {
  excludeAiGeneratedMessages,
} from '../utils/humanChatGuards';
import type {
  ChatModerationHold,
  Conversation,
  ConversationMember,
  CreateGroupConversationInput,
  EditMessageResult,
  ExtendedChatMessage,
  MessageContextValue,
  MessageState,
  NotificationPayload,
  OpenDirectConversationInput,
  SendConversationMessageInput,
  SendMessageResult,
} from '../types';

const SEND_FAILED: SendMessageResult = { message: null, heldForReview: false };

const PAGE_SIZE = CONVERSATIONS_PAGE_SIZE;
const MESSAGES_PAGE_SIZE = 20;

const initialState: MessageState = {
  conversations: [],
  loading: false,
  refreshing: false,
  loadingMore: false,
  hydrated: false,
  listLoadedOnce: false,
  error: null,
  page: 1,
  hasNextPage: false,
  searchTerm: '',
  listFilter: 'All',
  activeConversationId: null,
  messages: [],
  messagesLoading: false,
  messagesLoadingMore: false,
  messagesError: null,
  messagesPage: 1,
  messagesHasNextPage: false,
  sending: false,
  replyTo: null,
  unreadByConversationId: {},
  moderationHold: null,
};

const MESSAGE_ACTIONS = {
  LOADING: 'LOADING',
  REFRESHING: 'REFRESHING',
  LOADING_MORE: 'LOADING_MORE',
  SET_LIST: 'SET_LIST',
  APPEND_LIST: 'APPEND_LIST',
  ERROR: 'ERROR',
  HYDRATED: 'HYDRATED',
  SET_FILTER: 'SET_FILTER',
  PATCH_CONVERSATION: 'PATCH_CONVERSATION',
  REMOVE_CONVERSATION: 'REMOVE_CONVERSATION',
  UPSERT_CONVERSATION: 'UPSERT_CONVERSATION',
  SELECT_CONVERSATION: 'SELECT_CONVERSATION',
  MESSAGES_LOADING: 'MESSAGES_LOADING',
  MESSAGES_LOADING_MORE: 'MESSAGES_LOADING_MORE',
  SET_MESSAGES: 'SET_MESSAGES',
  APPEND_MESSAGES: 'APPEND_MESSAGES',
  MESSAGES_ERROR: 'MESSAGES_ERROR',
  SENDING: 'SENDING',
  PREPEND_MESSAGE: 'PREPEND_MESSAGE',
  MERGE_NEW_MESSAGES: 'MERGE_NEW_MESSAGES',
  REPLACE_MESSAGE: 'REPLACE_MESSAGE',
  REMOVE_MESSAGE: 'REMOVE_MESSAGE',
  SET_REPLY: 'SET_REPLY',
  SET_MODERATION_HOLD: 'SET_MODERATION_HOLD',
  RESET: 'RESET',
} as const;

type MessageAction =
  | { type: typeof MESSAGE_ACTIONS.LOADING }
  | { type: typeof MESSAGE_ACTIONS.REFRESHING }
  | { type: typeof MESSAGE_ACTIONS.LOADING_MORE }
  | {
      type: typeof MESSAGE_ACTIONS.SET_LIST;
      conversations: Conversation[];
      page: number;
      hasNextPage: boolean;
      searchTerm: string;
      listFilter: 'All' | 'Buddies';
    }
  | {
      type: typeof MESSAGE_ACTIONS.APPEND_LIST;
      conversations: Conversation[];
      page: number;
      hasNextPage: boolean;
    }
  | { type: typeof MESSAGE_ACTIONS.ERROR; error: string }
  | { type: typeof MESSAGE_ACTIONS.HYDRATED }
  | {
      type: typeof MESSAGE_ACTIONS.SET_FILTER;
      searchTerm: string;
      listFilter: 'All' | 'Buddies';
    }
  | {
      type: typeof MESSAGE_ACTIONS.PATCH_CONVERSATION;
      id: string;
      updates: Partial<Conversation>;
    }
  | { type: typeof MESSAGE_ACTIONS.REMOVE_CONVERSATION; id: string }
  | {
      type: typeof MESSAGE_ACTIONS.UPSERT_CONVERSATION;
      conversation: Conversation;
    }
  | {
      type: typeof MESSAGE_ACTIONS.SELECT_CONVERSATION;
      conversationId: string | null;
    }
  | { type: typeof MESSAGE_ACTIONS.MESSAGES_LOADING }
  | { type: typeof MESSAGE_ACTIONS.MESSAGES_LOADING_MORE }
  | {
      type: typeof MESSAGE_ACTIONS.SET_MESSAGES;
      messages: ExtendedChatMessage[];
      page: number;
      hasNextPage: boolean;
    }
  | {
      type: typeof MESSAGE_ACTIONS.APPEND_MESSAGES;
      messages: ExtendedChatMessage[];
      page: number;
      hasNextPage: boolean;
    }
  | { type: typeof MESSAGE_ACTIONS.MESSAGES_ERROR; error: string }
  | { type: typeof MESSAGE_ACTIONS.SENDING; sending: boolean }
  | {
      type: typeof MESSAGE_ACTIONS.PREPEND_MESSAGE;
      message: ExtendedChatMessage;
    }
  | {
      type: typeof MESSAGE_ACTIONS.MERGE_NEW_MESSAGES;
      messages: ExtendedChatMessage[];
    }
  | {
      type: typeof MESSAGE_ACTIONS.REPLACE_MESSAGE;
      messageId: string | number;
      message: ExtendedChatMessage;
    }
  | {
      type: typeof MESSAGE_ACTIONS.REMOVE_MESSAGE;
      messageId: string | number;
    }
  | {
      type: typeof MESSAGE_ACTIONS.SET_REPLY;
      replyTo: ExtendedChatMessage | null;
    }
  | {
      type: typeof MESSAGE_ACTIONS.SET_MODERATION_HOLD;
      moderationHold: ChatModerationHold | null;
    }
  | { type: typeof MESSAGE_ACTIONS.RESET };

/** Identity an optimistic row shares with the server message confirming it. */
const optimisticMatchKey = (message: ExtendedChatMessage): string =>
  `${String(message.user?._id ?? '')}|${(message.text ?? '').trim()}`;

const optimisticTextKey = (message: ExtendedChatMessage): string =>
  (message.text ?? '').trim();

const hasMessageSender = (message: ExtendedChatMessage): boolean => {
  const id = message.user?._id;
  return id != null && String(id).trim() !== '';
};

const messageReducer = (
  state: MessageState,
  action: MessageAction,
): MessageState => {
  switch (action.type) {
    case MESSAGE_ACTIONS.LOADING:
      return { ...state, loading: true, error: null };
    case MESSAGE_ACTIONS.REFRESHING:
      return { ...state, refreshing: true, error: null };
    case MESSAGE_ACTIONS.LOADING_MORE:
      return { ...state, loadingMore: true, error: null };
    case MESSAGE_ACTIONS.SET_LIST: {
      const previousById = new Map(
        state.conversations.map(c => [String(c.id), c]),
      );
      const conversations = action.conversations.map(incoming => {
        const previous = previousById.get(String(incoming.id));
        const withUnread = applyUnreadToConversation(incoming, previous);
        if (!previous || withUnread.category !== 'groups') {
          return withUnread;
        }

        // Prefer backend avatar when present; keep last known remote avatar if
        // list payload temporarily omits avatarUrl (common on first paint / refresh).
        const incomingAvatar =
          typeof withUnread.avatar === 'string' ? withUnread.avatar.trim() : '';
        const previousAvatar =
          typeof previous.avatar === 'string' ? previous.avatar.trim() : '';
        const keepPreviousAvatar =
          !incomingAvatar &&
          !!previousAvatar &&
          (previousAvatar.startsWith('http://') ||
            previousAvatar.startsWith('https://'));

        const keepPreviousMembers =
          (!withUnread.members || withUnread.members.length === 0) &&
          !!previous.members?.length;

        // Keep a locally renamed title when the list payload still has the old name.
        const previousName = previous.name?.trim() || '';
        const previousGroupName = previous.groupName?.trim() || '';
        const incomingName = withUnread.name?.trim() || '';
        const keepPreviousName =
          !!previousName &&
          !!previousGroupName &&
          previousName === previousGroupName &&
          previousName !== incomingName;

        if (!keepPreviousAvatar && !keepPreviousMembers && !keepPreviousName) {
          return withUnread;
        }

        return {
          ...withUnread,
          avatar: keepPreviousAvatar ? previous.avatar : withUnread.avatar,
          members: keepPreviousMembers ? previous.members : withUnread.members,
          memberCount: keepPreviousMembers
            ? previous.memberCount ?? previous.members?.length
            : withUnread.memberCount,
          name: keepPreviousName ? previous.name : withUnread.name,
          groupName: keepPreviousName ? previous.groupName : withUnread.groupName,
        };
      });

      return {
        ...state,
        loading: false,
        refreshing: false,
        loadingMore: false,
        listLoadedOnce: true,
        conversations,
        unreadByConversationId: mergeUnreadByConversationId(
          state.unreadByConversationId,
          conversations,
        ),
        page: action.page,
        hasNextPage: action.hasNextPage,
        searchTerm: action.searchTerm,
        listFilter: action.listFilter,
        error: null,
      };
    }
    case MESSAGE_ACTIONS.APPEND_LIST: {
      const existingIds = new Set(state.conversations.map(c => c.id));
      const added = action.conversations
        .filter(c => !existingIds.has(c.id))
        .map(c => applyUnreadToConversation(c));
      const merged = [...state.conversations, ...added];
      return {
        ...state,
        loadingMore: false,
        conversations: merged,
        unreadByConversationId: mergeUnreadByConversationId(
          state.unreadByConversationId,
          added,
        ),
        page: action.page,
        hasNextPage: action.hasNextPage,
      };
    }
    case MESSAGE_ACTIONS.ERROR:
      return {
        ...state,
        loading: false,
        refreshing: false,
        loadingMore: false,
        error: action.error,
      };
    case MESSAGE_ACTIONS.HYDRATED:
      return { ...state, hydrated: true, loading: false };
    case MESSAGE_ACTIONS.SET_FILTER:
      return {
        ...state,
        searchTerm: action.searchTerm,
        listFilter: action.listFilter,
      };
    case MESSAGE_ACTIONS.PATCH_CONVERSATION: {
      const conversations = state.conversations.map(c =>
        String(c.id) === String(action.id)
          ? normalizeConversation({ ...c, ...action.updates })
          : c,
      );
      const patched = conversations.find(
        c => String(c.id) === String(action.id),
      );
      return {
        ...state,
        conversations,
        unreadByConversationId: patched
          ? mergeUnreadByConversationId(state.unreadByConversationId, [patched])
          : state.unreadByConversationId,
      };
    }
    case MESSAGE_ACTIONS.REMOVE_CONVERSATION: {
      const nextUnread = { ...state.unreadByConversationId };
      delete nextUnread[action.id];
      return {
        ...state,
        conversations: state.conversations.filter(
          c => String(c.id) !== String(action.id),
        ),
        unreadByConversationId: nextUnread,
      };
    }
    case MESSAGE_ACTIONS.UPSERT_CONVERSATION: {
      const previous = state.conversations.find(
        c => c.id === action.conversation.id,
      );
      const incoming = applyUnreadToConversation(
        normalizeConversation(action.conversation),
        previous,
      );
      const exists = Boolean(previous);
      const next = exists
        ? state.conversations.map(c =>
            c.id === incoming.id
              ? normalizeConversation({ ...c, ...incoming })
              : c,
          )
        : [incoming, ...state.conversations];
      return {
        ...state,
        conversations: next,
        unreadByConversationId: mergeUnreadByConversationId(
          state.unreadByConversationId,
          [incoming],
        ),
      };
    }
    case MESSAGE_ACTIONS.SELECT_CONVERSATION: {
      // Leaving a thread must drop "viewing" so later messages count as unread,
      // but keep the buffer so the closing screen does not flash empty.
      if (action.conversationId == null) {
        return {
          ...state,
          activeConversationId: null,
          replyTo: null,
        };
      }
      const changed = action.conversationId !== state.activeConversationId;
      return {
        ...state,
        activeConversationId: action.conversationId,
        messages: changed ? [] : state.messages,
        messagesLoading: changed ? true : state.messagesLoading,
        messagesLoadingMore: changed ? false : state.messagesLoadingMore,
        messagesError: null,
        replyTo: resolveReplyAfterConversationSelection(
          state.replyTo,
          state.activeConversationId,
          action.conversationId,
        ),
      };
    }
    case MESSAGE_ACTIONS.MESSAGES_LOADING:
      return {
        ...state,
        messagesLoading: true,
        messagesError: null,
      };
    case MESSAGE_ACTIONS.MESSAGES_LOADING_MORE:
      return { ...state, messagesLoadingMore: true, messagesError: null };
    case MESSAGE_ACTIONS.SET_MESSAGES:
      return {
        ...state,
        messagesLoading: false,
        messagesLoadingMore: false,
        messages: action.messages,
        messagesPage: action.page,
        messagesHasNextPage: action.hasNextPage,
        messagesError: null,
      };
    case MESSAGE_ACTIONS.APPEND_MESSAGES: {
      const existingIds = new Set(state.messages.map(m => String(m._id)));
      const older = action.messages.filter(
        m => !existingIds.has(String(m._id)),
      );
      return {
        ...state,
        messagesLoadingMore: false,
        messages: [...state.messages, ...older],
        messagesPage: action.page,
        messagesHasNextPage: action.hasNextPage,
      };
    }
    case MESSAGE_ACTIONS.MESSAGES_ERROR:
      return {
        ...state,
        messagesLoading: false,
        messagesLoadingMore: false,
        messagesError: action.error,
      };
    case MESSAGE_ACTIONS.SENDING:
      return { ...state, sending: action.sending };
    case MESSAGE_ACTIONS.PREPEND_MESSAGE: {
      const incomingId = String(action.message._id);
      if (state.messages.some(m => String(m._id) === incomingId)) {
        return state;
      }
      return {
        ...state,
        messages: [action.message, ...state.messages],
      };
    }
    case MESSAGE_ACTIONS.MERGE_NEW_MESSAGES: {
      const existingIds = new Set(state.messages.map(m => String(m._id)));
      const fresh = action.messages.filter(
        m => !existingIds.has(String(m._id)),
      );
      if (!fresh.length) return state;
      // An optimistic row carries no server id, so it can only be matched by
      // author and body. Drop one local row per matching fresh message rather
      // than clearing them all, which would hide sends this batch never
      // covered.
      const unmatched = new Map<string, number>();
      for (const message of fresh) {
        const key = optimisticMatchKey(message);
        unmatched.set(key, (unmatched.get(key) ?? 0) + 1);
      }
      const retained = state.messages.filter(m => {
        if (!isLocalMessageId(m._id)) return true;

        const key = optimisticMatchKey(m);
        const remaining = unmatched.get(key) ?? 0;
        if (remaining > 0) {
          unmatched.set(key, remaining - 1);
          return false;
        }

        if (hasMessageSender(m)) {
          return true;
        }
        const text = optimisticTextKey(m);
        if (!text) {
          return true;
        }
        for (const [freshKey, count] of unmatched.entries()) {
          if (count <= 0) {
            continue;
          }
          const freshText = freshKey.slice(freshKey.indexOf('|') + 1);
          if (freshText === text) {
            unmatched.set(freshKey, count - 1);
            return false;
          }
        }
        return true;
      });
      return {
        ...state,
        messagesLoading: false,
        // GiftedChat.append was removed in the monorepo refactor; prepend fresh
        // messages to the front of the list (newest-first), mirroring PREPEND_MESSAGE.
        messages: [...fresh, ...retained],
      };
    }
    case MESSAGE_ACTIONS.REPLACE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map(m =>
          String(m._id) === String(action.messageId) ? action.message : m,
        ),
      };
    case MESSAGE_ACTIONS.REMOVE_MESSAGE:
      return {
        ...state,
        messages: state.messages.filter(
          m => String(m._id) !== String(action.messageId),
        ),
      };
    case MESSAGE_ACTIONS.SET_REPLY:
      return { ...state, replyTo: action.replyTo };
    case MESSAGE_ACTIONS.SET_MODERATION_HOLD:
      // Clearing is the common case — the composer clears on every keystroke
      // and every send clears before trying. Returning the same state when
      // there is nothing to clear keeps that off the re-render path.
      if (state.moderationHold === action.moderationHold) {
        return state;
      }
      return { ...state, moderationHold: action.moderationHold };
    case MESSAGE_ACTIONS.RESET:
      return { ...initialState };
    default:
      return state;
  }
};

const PREF_KEYS: Array<keyof Conversation> = [
  'isPinned',
  'pinnedAt',
  'isMuted',
  'isFavourite',
  'isBlocked',
  'name',
  'groupName',
  'username',
  'userName',
  'peerUserId',
  'peerUserGuid',
  // Cache collage inputs so group avatars survive list payloads that omit participants.
  'members',
  'memberCount',
  'avatar',
];

/** Max parallel participant fetches after a list load (avoid stampedes). */
const GROUP_MEMBER_HYDRATE_CONCURRENCY = 4;
const DIRECT_IDENTITY_HYDRATE_CONCURRENCY = 4;
const UNREAD_HYDRATE_CONCURRENCY = 4;

const pickPrefs = (conversation: Conversation): Partial<Conversation> => {
  const prefs: Partial<Conversation> = {};
  for (const key of PREF_KEYS) {
    if (conversation[key] !== undefined) {
      (prefs as Record<string, unknown>)[key] = conversation[key];
    }
  }
  return prefs;
};

const messageContext = createContext<MessageContextValue | null>(null);

export interface MessageProviderProps {
  /**
   * Skips the conversation list request during bootstrap, leaving it to
   * `ensureConversationsLoaded` / an explicit `fetchConversations`. Platforms
   * with no push channel poll the list themselves and only need it where the
   * inbox is on screen; platforms that surface a global unread badge must keep
   * the default so the badge is populated from app start.
   */
  deferInitialFetch?: boolean;
}

export const MessageProvider = ({
  children,
  deferInitialFetch = false,
}: PropsWithChildren<MessageProviderProps>): React.JSX.Element => {
  const [state, dispatch] = useReducer(messageReducer, initialState);
  const { userId } = useAuth();
  const { profile } = useProfile();
  const { t } = useTranslation();
  const prefsRef = useRef<ConversationPrefsMap>({});
  const stateRef = useRef(state);
  stateRef.current = state;
  const sendingInFlightRef = useRef(false);
  /** First-page list requests in flight, keyed by `filter|searchTerm`. */
  const listRequestsRef = useRef<Map<string, Promise<void>>>(new Map());
  /** Conversation ids currently fetching participants for collage avatars. */
  const groupMemberHydrateInFlightRef = useRef<Set<string>>(new Set());
  const hydrateMissingGroupMembersRef = useRef<
    (conversations: Conversation[]) => void
  >(() => {});
  /** Direct chats whose sparse list rows are being resolved to peer usernames. */
  const directIdentityHydrateInFlightRef = useRef<Set<string>>(new Set());
  const hydrateMissingDirectIdentitiesRef = useRef<
    (conversations: Conversation[]) => void
  >(() => {});
  const unreadHydrateInFlightRef = useRef<Set<string>>(new Set());
  const unreadHydratedKeyRef = useRef<Record<string, string>>({});
  const locallyReadMessageIdRef = useRef<Record<string, string>>({});
  /**
   * Thread state the backend has already accepted a receipt for, keyed by
   * conversation. Holding the fingerprint rather than message ids means a new
   * message re-arms the receipt while re-opening an unchanged thread does not
   * repeat it.
   */
  const readReceiptsSentRef = useRef<Record<string, string>>({});
  const unreadHydrateGenerationRef = useRef(0);
  const hydrateUnreadCountsRef = useRef<
    (conversations: Conversation[]) => void
  >(() => {});

  const aiAgentUserId = profile?.followedAiAgent?.agentId ?? null;
  const robiConversationId =
    profile?.followedAiAgent?.conversationId != null
      ? String(profile.followedAiAgent.conversationId)
      : null;

  const persistPrefs = useCallback(async (prefs: ConversationPrefsMap) => {
    prefsRef.current = prefs;
    await storage.setItem(STORAGE_KEYS.CONVERSATION_PREFS, prefs);
  }, []);

  const updatePrefsFor = useCallback(
    async (id: string, updates: Partial<Conversation>) => {
      const key = String(id);
      const next = {
        ...prefsRef.current,
        [key]: {
          ...prefsRef.current[key],
          ...pickPrefs({
            id: key,
            name: '',
            ...prefsRef.current[key],
            ...updates,
          }),
        },
      };
      // Update ref synchronously so in-flight syncs see the renamed title.
      prefsRef.current = next;
      await persistPrefs(next);
    },
    [persistPrefs],
  );

  const findDirectConversationByUserId = useCallback(
    (peerUserId: number | string): Conversation | null => {
      const target = String(peerUserId);
      const agentId =
        aiAgentUserId != null ? String(aiAgentUserId) : null;
      return (
        stateRef.current.conversations.find(c => {
          if (c.category === 'groups' || c.category === 'ai') return false;
          if (isAiConversationType(c.conversationType)) return false;
          // Never treat the Robi/AgentDirect thread as a human DM.
          if (robiConversationId && String(c.id) === robiConversationId) {
            return false;
          }
          if (agentId && String(c.peerUserId) === agentId) return false;
          if (agentId && String(c.id) === agentId) return false;
          if (c.peerUserId && String(c.peerUserId) === target) {
            // Skip threads that still list the AI agent as a participant.
            if (
              agentId &&
              (c.participantUserIds ?? []).some(id => String(id) === agentId)
            ) {
              return false;
            }
            return true;
          }
          return (c.participantUserIds ?? []).some(
            id =>
              String(id) === target &&
              String(id) !== String(userId) &&
              String(id) !== agentId,
          );
        }) ?? null
      );
    },
    [userId, aiAgentUserId, robiConversationId],
  );

  const stripAiAgentIfPresent = useCallback(
    async (conversationId: string) => {
      if (!aiAgentUserId) return;
      if (robiConversationId && String(conversationId) === robiConversationId) {
        return;
      }
      const existing = stateRef.current.conversations.find(
        c => String(c.id) === String(conversationId),
      );
      if (existing && isAiConversationType(existing.conversationType)) {
        return;
      }
      await messageService.ensureNoAiAgentInConversation({
        conversationId,
        aiAgentUserId,
        currentUserId: userId,
      });
    },
    [aiAgentUserId, robiConversationId, userId],
  );

  // Depend on state.conversations so consumers re-render when a chat is patched
  // (rename, members, avatar). Reading only stateRef would freeze derived UI.
  const getConversationById = useCallback(
    (id: string): Conversation | null => {
      return (
        state.conversations.find(c => String(c.id) === String(id)) ?? null
      );
    },
    [state.conversations],
  );

  const fetchConversations = useCallback(
    async (options?: {
      refresh?: boolean;
      searchTerm?: string;
      filter?: 'All' | 'Buddies';
      silent?: boolean;
    }) => {
      const searchTerm =
        options?.searchTerm ?? stateRef.current.searchTerm ?? '';
      const filter = options?.filter ?? stateRef.current.listFilter ?? 'All';
      const isRefresh = !!options?.refresh;
      const silent = options?.silent === true;

      // Two callers asking for the same slice at once — a screen mounting while
      // the poll fires, a remount replaying its effect — get one request and
      // one answer instead of a race whose loser still overwrites the list.
      const key = `${filter}|${searchTerm.trim()}`;
      const pending = listRequestsRef.current.get(key);
      if (pending) return pending;

      const load = async (): Promise<void> => {
        // A silent load feeds a badge, not a screen: it must not raise the
        // list's loading flags (a skeleton would flash over rows the member is
        // reading) nor replace them with an error state on failure.
        if (!silent) {
          dispatch({
            type: isRefresh ? MESSAGE_ACTIONS.REFRESHING : MESSAGE_ACTIONS.LOADING,
          });
        }

        try {
          const result = await messageService.getConversations({
            page: 1,
            pageSize: PAGE_SIZE,
            searchTerm: searchTerm.trim() || undefined,
            filter,
            currentUserId: userId,
            prefs: prefsRef.current,
          });
          dispatch({
            type: MESSAGE_ACTIONS.SET_LIST,
            conversations: result.data,
            page: 1,
            hasNextPage: result.hasNextPage,
            searchTerm,
            listFilter: filter,
          });
          // List payloads often omit participants; fill collage members in the background.
          hydrateMissingGroupMembersRef.current(result.data);
          hydrateMissingDirectIdentitiesRef.current(result.data);
          hydrateUnreadCountsRef.current(result.data);
        } catch (error) {
          if (silent) return;
          dispatch({
            type: MESSAGE_ACTIONS.ERROR,
            error: getUserFacingErrorMessage(
              error,
              i18n.t('messages.errors.loadFailed'),
            ),
          });
        }
      };

      const request = load().finally(() => {
        listRequestsRef.current.delete(key);
      });
      listRequestsRef.current.set(key, request);
      return request;
    },
    [userId],
  );

  const ensureConversationsLoaded = useCallback(async (): Promise<void> => {
    if (stateRef.current.listLoadedOnce) return;
    // Always the unfiltered first page: this is the list other features dedupe
    // against and read rows from, never the slice a search box last asked for.
    // Concurrent callers collapse into one request inside fetchConversations.
    return fetchConversations({
      refresh: false,
      searchTerm: '',
      filter: 'All',
    });
  }, [fetchConversations]);

  const loadMoreConversations = useCallback(async () => {
    const current = stateRef.current;
    if (current.loadingMore || !current.hasNextPage || current.loading) {
      return;
    }
    dispatch({ type: MESSAGE_ACTIONS.LOADING_MORE });
    try {
      const nextPage = current.page + 1;
      const result = await messageService.getConversations({
        page: nextPage,
        pageSize: PAGE_SIZE,
        searchTerm: current.searchTerm.trim() || undefined,
        filter: current.listFilter,
        currentUserId: userId,
        prefs: prefsRef.current,
      });
      dispatch({
        type: MESSAGE_ACTIONS.APPEND_LIST,
        conversations: result.data,
        page: nextPage,
        hasNextPage: result.hasNextPage,
      });
      hydrateMissingGroupMembersRef.current(result.data);
      hydrateMissingDirectIdentitiesRef.current(result.data);
      hydrateUnreadCountsRef.current(result.data);
    } catch (error) {
      dispatch({
        type: MESSAGE_ACTIONS.ERROR,
        error: getUserFacingErrorMessage(
          error,
          i18n.t('messages.errors.loadFailed'),
        ),
      });
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      prefsRef.current = {};
      groupMemberHydrateInFlightRef.current.clear();
      directIdentityHydrateInFlightRef.current.clear();
      unreadHydrateInFlightRef.current.clear();
      unreadHydratedKeyRef.current = {};
      locallyReadMessageIdRef.current = {};
      readReceiptsSentRef.current = {};
      unreadHydrateGenerationRef.current += 1;
      dispatch({ type: MESSAGE_ACTIONS.RESET });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const storedPrefs =
          (await storage.getItem<ConversationPrefsMap>(
            STORAGE_KEYS.CONVERSATION_PREFS,
            {},
          )) || {};
        if (cancelled) return;
        prefsRef.current = storedPrefs;

        // Migrate legacy full-conversation cache into prefs, then drop it.
        const legacy = await storage.getItem<Conversation[]>(
          STORAGE_KEYS.CONVERSATIONS,
          null,
        );
        if (cancelled) return;

        if (Array.isArray(legacy) && legacy.length > 0) {
          const migrated: ConversationPrefsMap = { ...storedPrefs };
          legacy.forEach(c => {
            migrated[c.id] = { ...migrated[c.id], ...pickPrefs(c) };
          });
          await persistPrefs(migrated);
          await storage.removeItem(STORAGE_KEYS.CONVERSATIONS);
          if (cancelled) return;
        }

        if (!deferInitialFetch) {
          await fetchConversations({ refresh: false });
        }
      } catch {
        if (!cancelled) {
          dispatch({
            type: MESSAGE_ACTIONS.ERROR,
            error: t('messages.errors.loadFailed'),
          });
        }
      } finally {
        if (!cancelled) {
          dispatch({ type: MESSAGE_ACTIONS.HYDRATED });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, fetchConversations, persistPrefs, t, deferInitialFetch]);

  useEffect(() => {
    const sub = eventBus.addListener(USER_SESSION_CLEARED_EVENT, () => {
      prefsRef.current = {};
      groupMemberHydrateInFlightRef.current.clear();
      directIdentityHydrateInFlightRef.current.clear();
      unreadHydrateInFlightRef.current.clear();
      unreadHydratedKeyRef.current = {};
      locallyReadMessageIdRef.current = {};
      readReceiptsSentRef.current = {};
      unreadHydrateGenerationRef.current += 1;
      dispatch({ type: MESSAGE_ACTIONS.RESET });
    });
    return () => sub.remove();
  }, []);

  const updateConversation = useCallback(
    async (id: string, updates: Partial<Conversation>) => {
      dispatch({ type: MESSAGE_ACTIONS.PATCH_CONVERSATION, id, updates });
      await updatePrefsFor(id, updates);
    },
    [updatePrefsFor],
  );

  /**
   * Fetch participants for group chats that have no custom avatar and no
   * members yet (needed for the list collage). Results are patched into the
   * list and persisted in prefs so the next open does not refetch.
   */
  const hydrateMissingGroupMembers = useCallback(
    async (conversations: Conversation[]) => {
      const previousById = new Map(
        stateRef.current.conversations.map(c => [String(c.id), c]),
      );

      const targets = conversations.filter(incoming => {
        const previous = previousById.get(String(incoming.id));
        const merged: Conversation = {
          ...incoming,
          members:
            incoming.members?.length
              ? incoming.members
              : previous?.members?.length
                ? previous.members
                : incoming.members,
          avatar: hasUsableGroupAvatar(incoming.avatar)
            ? incoming.avatar
            : previous?.avatar ?? incoming.avatar,
        };
        if (!needsGroupCollageHydration(merged)) return false;
        return !groupMemberHydrateInFlightRef.current.has(String(incoming.id));
      });

      if (targets.length === 0) return;

      for (let i = 0; i < targets.length; i += GROUP_MEMBER_HYDRATE_CONCURRENCY) {
        const batch = targets.slice(i, i + GROUP_MEMBER_HYDRATE_CONCURRENCY);
        await Promise.all(
          batch.map(async conversation => {
            const id = String(conversation.id);
            groupMemberHydrateInFlightRef.current.add(id);
            try {
              const members = await messageService.getParticipants(id);
              if (!members.length) return;

              const current = stateRef.current.conversations.find(
                c => String(c.id) === id,
              );
              // Another path (open chat / sync) may have already filled members.
              if (current?.members && current.members.length > 0) {
                // Still persist if prefs never cached them.
                const prefs = prefsRef.current[id];
                if (!prefs?.members?.length) {
                  await updatePrefsFor(id, {
                    members: current.members,
                    memberCount: current.members.length,
                  });
                }
                return;
              }

              await updateConversation(id, {
                members,
                memberCount: members.length,
              });
            } catch {
              // Keep list usable; collage stays on name initial until next attempt.
            } finally {
              groupMemberHydrateInFlightRef.current.delete(id);
            }
          }),
        );
      }
    },
    [updateConversation, updatePrefsFor],
  );
  hydrateMissingGroupMembersRef.current = hydrateMissingGroupMembers;

  /**
   * Conversation-list payloads can omit direct-chat participants. Resolve the
   * peer from the participants endpoint so rows and open headers show the
   * username rather than a generic "User" / "Chat" placeholder.
   */
  const hydrateMissingDirectIdentities = useCallback(
    async (conversations: Conversation[]) => {
      const previousById = new Map(
        stateRef.current.conversations.map(c => [String(c.id), c]),
      );
      const targets = conversations.filter(incoming => {
        const previous = previousById.get(String(incoming.id));
        const username =
          incoming.userName?.trim() ||
          incoming.username?.trim() ||
          previous?.userName?.trim() ||
          previous?.username?.trim();
        const isDirect =
          incoming.category !== 'groups' &&
          !isGroupConversationType(incoming.conversationType) &&
          incoming.category !== 'ai' &&
          !isAiConversationType(incoming.conversationType);
        return (
          isDirect &&
          !username &&
          !directIdentityHydrateInFlightRef.current.has(String(incoming.id))
        );
      });

      for (let index = 0; index < targets.length; index += DIRECT_IDENTITY_HYDRATE_CONCURRENCY) {
        const batch = targets.slice(index, index + DIRECT_IDENTITY_HYDRATE_CONCURRENCY);
        await Promise.all(
          batch.map(async conversation => {
            const id = String(conversation.id);
            directIdentityHydrateInFlightRef.current.add(id);
            try {
              const members = await messageService.getParticipants(id);
              const agentId = aiAgentUserId != null ? String(aiAgentUserId) : null;
              const peer = members.find(member =>
                (userId == null || String(member.id) !== String(userId)) &&
                (!agentId || String(member.id) !== agentId),
              );
              const peerHandle = peer?.username?.trim() || peer?.name?.trim();
              if (!peer || !peerHandle) return;

              const current = stateRef.current.conversations.find(
                item => String(item.id) === id,
              );
              await updateConversation(id, {
                username: peerHandle,
                userName: peerHandle,
                name: peerHandle,
                avatar: peer.avatar || current?.avatar || null,
                members,
                memberCount: members.length,
                peerUserId: peer.id,
                peerUserGuid: peer.profileUserGuid ?? null,
              });
            } catch {
              // Keep the list usable; a later refresh or opened thread retries.
            } finally {
              directIdentityHydrateInFlightRef.current.delete(id);
            }
          }),
        );
      }
    },
    [aiAgentUserId, updateConversation, userId],
  );
  hydrateMissingDirectIdentitiesRef.current = hydrateMissingDirectIdentities;

  /** After a list load, fetch UnreadCount for threads that still need a badge. */
  const hydrateUnreadCounts = useCallback(
    async (conversations: Conversation[]) => {
      const isViewing = (conversationId: string): boolean =>
        String(stateRef.current.activeConversationId ?? '') ===
        String(conversationId);

      const targets = conversations.filter(conversation => {
        if (!shouldCountConversationUnread(conversation)) return false;
        // The list already answered this row's badge; asking again per
        // conversation would be one request each for a number we hold.
        if (conversation.unreadCountReported) return false;
        const id = String(conversation.id);
        if (isViewing(id)) return false;
        const key = conversationUnreadFingerprint(conversation);
        if (locallyReadMessageIdRef.current[id] === key) return false;
        if (unreadHydratedKeyRef.current[id] === key) return false;
        return !unreadHydrateInFlightRef.current.has(id);
      });

      if (targets.length === 0) return;

      const generation = unreadHydrateGenerationRef.current;

      for (let i = 0; i < targets.length; i += UNREAD_HYDRATE_CONCURRENCY) {
        const batch = targets.slice(i, i + UNREAD_HYDRATE_CONCURRENCY);
        await Promise.all(
          batch.map(async conversation => {
            const id = String(conversation.id);
            const startedKey = conversationUnreadFingerprint(conversation);
            unreadHydrateInFlightRef.current.add(id);
            try {
              const count = await messageService.getUnreadCount(id);
              if (generation !== unreadHydrateGenerationRef.current) return;
              if (isViewing(id)) return;

              const current = stateRef.current.conversations.find(
                row => String(row.id) === id,
              );
              const currentKey = current
                ? conversationUnreadFingerprint(current)
                : startedKey;
              if (locallyReadMessageIdRef.current[id] === currentKey) return;
              if (current && currentKey !== startedKey) return;

              unreadHydratedKeyRef.current[id] = currentKey;
              await updateConversation(id, {
                unreadCount: count,
                isUnread: count > 0,
              });
            } catch {
              // Keep last known badge if UnreadCount fails.
            } finally {
              unreadHydrateInFlightRef.current.delete(id);
            }
          }),
        );
      }
    },
    [updateConversation],
  );
  hydrateUnreadCountsRef.current = hydrateUnreadCounts;

  /** Refresh a single conversation from the API and merge into the list (avatar/name/members). */
  const syncConversationDetails = useCallback(
    async (conversationId: string): Promise<Conversation | null> => {
      if (!conversationId) return null;
      try {
        const fresh = await messageService.getConversationById(
          conversationId,
          userId,
          prefsRef.current[String(conversationId)],
        );
        const localPrefs = prefsRef.current[String(conversationId)];
        const existing = stateRef.current.conversations.find(
          c => String(c.id) === String(conversationId),
        );
        const isGroup =
          fresh.category === 'groups' ||
          isGroupConversationType(fresh.conversationType);
        const directUsername =
          fresh.username?.trim() ||
          existing?.username?.trim() ||
          null;
        const preferredName =
          isGroup
            ? localPrefs?.name?.trim() || existing?.name?.trim() || fresh.name
            : directUsername || fresh.name;
        const preferredGroupName =
          localPrefs?.groupName?.trim() ||
          existing?.groupName?.trim() ||
          fresh.groupName;
        await updateConversation(conversationId, {
          avatar: fresh.avatar ?? existing?.avatar ?? null,
          // Keep a locally renamed title when prefs/state already have one.
          name: preferredName,
          username: isGroup ? null : directUsername,
          groupName: preferredGroupName,
          members:
            (fresh.members?.length ?? 0) > 0
              ? fresh.members
              : existing?.members ?? fresh.members,
          memberCount:
            fresh.memberCount ??
            fresh.members?.length ??
            existing?.memberCount,
          createdBy: fresh.createdBy,
          conversationType: fresh.conversationType,
          participantUserIds: fresh.participantUserIds,
          peerUserId: fresh.peerUserId ?? existing?.peerUserId ?? null,
          peerUserGuid: fresh.peerUserGuid ?? existing?.peerUserGuid ?? null,
        });
        return fresh;
      } catch {
        return null;
      }
    },
    [userId, updateConversation],
  );

  const togglePin = useCallback(
    (id: string) => {
      const conversation = stateRef.current.conversations.find(c => c.id === id);
      if (!conversation) return;
      const nextPinned = !conversation.isPinned;
      updateConversation(id, {
        isPinned: nextPinned,
        pinnedAt: nextPinned ? Date.now() : undefined,
      });
    },
    [updateConversation],
  );

  const toggleMute = useCallback(
    (id: string) => {
      const conversation = stateRef.current.conversations.find(c => c.id === id);
      if (!conversation) return;
      updateConversation(id, { isMuted: !conversation.isMuted });
    },
    [updateConversation],
  );

  const toggleFavourite = useCallback(
    (id: string) => {
      const conversation = stateRef.current.conversations.find(c => c.id === id);
      if (!conversation) return;
      updateConversation(id, { isFavourite: !conversation.isFavourite });
    },
    [updateConversation],
  );

  const blockConversation = useCallback(
    async (id: string, overridePeerUserId?: number | string | null) => {
      const conversation = stateRef.current.conversations.find(
        c => String(c.id) === String(id),
      );
      let peerUserId: number | null =
        overridePeerUserId != null ? Number(overridePeerUserId) : null;

      if (!peerUserId && conversation?.peerUserId) {
        peerUserId = Number(conversation.peerUserId);
      }
      if (!peerUserId && userId) {
        const fromIds = (conversation?.participantUserIds ?? []).find(
          p => String(p) !== String(userId),
        );
        if (fromIds) peerUserId = Number(fromIds);
      }
      if (!peerUserId && userId) {
        const fromMembers = (conversation?.members ?? []).find(
          m => String(m.id) !== String(userId),
        );
        if (fromMembers && Number.isFinite(Number(fromMembers.id))) {
          peerUserId = Number(fromMembers.id);
        }
      }
      if (!peerUserId) {
        throw new Error(i18n.t('chat.errors.blockPeerUnresolved'));
      }

      await messageService.blockConversation(id, peerUserId);
      if (conversation) {
        await updateConversation(id, { isBlocked: true });
      }
    },
    [userId, updateConversation],
  );

  const deleteConversation = useCallback(
    async (id: string, leaveConversation = true) => {
      await messageService.deleteConversation(id, leaveConversation);
      dispatch({ type: MESSAGE_ACTIONS.REMOVE_CONVERSATION, id });
      const nextPrefs = { ...prefsRef.current };
      delete nextPrefs[id];
      await persistPrefs(nextPrefs);
      if (stateRef.current.activeConversationId === id) {
        dispatch({
          type: MESSAGE_ACTIONS.SELECT_CONVERSATION,
          conversationId: null,
        });
      }
    },
    [persistPrefs],
  );

  const markConversationRead = useCallback(
    async (id: string) => {
      const conversation = stateRef.current.conversations.find(c => c.id === id);
      const fingerprint = conversation
        ? conversationUnreadFingerprint(conversation)
        : '';
      locallyReadMessageIdRef.current[id] = fingerprint;
      unreadHydratedKeyRef.current[id] = fingerprint;

      // Claim the receipt before the first await: opening a thread runs this
      // from the inbox row and again from the thread itself, and a guard that
      // only closes once the response lands lets every caller through.
      const alreadySent = readReceiptsSentRef.current[id] === fingerprint;
      if (!alreadySent) readReceiptsSentRef.current[id] = fingerprint;

      await updateConversation(id, { isUnread: false, unreadCount: 0 });
      if (alreadySent) return;

      // MarkMessageRead clears the whole thread server-side, so the receipt
      // needs nothing from the message list. It was once addressed per message,
      // and the leftover bookkeeping is what broke this: a thread opened from
      // the inbox has no messages loaded yet, and the list payload carries no
      // lastMessageId, so there was never anything to acknowledge and the call
      // was skipped entirely — clearing the badge locally while the server kept
      // counting it, until a reload put the count back.
      try {
        await messageService.markMessageRead(id);
      } catch {
        // Let a later open retry; the badge is already cleared locally.
        if (readReceiptsSentRef.current[id] === fingerprint) {
          delete readReceiptsSentRef.current[id];
        }
      }
    },
    [updateConversation],
  );

  const selectConversation = useCallback(
    (conversationId: string | null, options?: { onlyIfActive?: string }) => {
      if (
        options?.onlyIfActive != null &&
        String(stateRef.current.activeConversationId ?? '') !==
          String(options.onlyIfActive)
      ) {
        return;
      }
      dispatch({
        type: MESSAGE_ACTIONS.SELECT_CONVERSATION,
        conversationId,
      });
    },
    [],
  );

  /** Conversation ids whose message page is being fetched right now. */
  const messageRequestsRef = useRef<Set<string>>(new Set());

  const fetchMessages = useCallback(
    async (
      conversationId: string,
      options?: { refresh?: boolean; silent?: boolean },
    ) => {
      if (!conversationId) return;
      const silent = options?.silent === true;
      const requestKey = String(conversationId);

      dispatch({
        type: MESSAGE_ACTIONS.SELECT_CONVERSATION,
        conversationId,
      });

      // Duplicate loads are detected from the requests actually in flight, not
      // from `messagesLoading`: selecting a thread raises that flag before
      // anyone fetches (the inbox selects a row, then routes to the chat), so
      // reading it here strands the thread on its skeleton forever.
      const inFlight = messageRequestsRef.current.has(requestKey);
      if (inFlight && (silent || !options?.refresh)) {
        return;
      }

      messageRequestsRef.current.add(requestKey);
      try {
        // On first (non-silent) load of a human chat, remove any AI agent participant
        // so subsequent SendMessage calls do not trigger backend auto-replies.
        if (!silent) {
          await stripAiAgentIfPresent(conversationId);
          dispatch({ type: MESSAGE_ACTIONS.MESSAGES_LOADING });
        }

        const result = await messageService.getMessages({
          conversationId,
          page: 1,
          pageSize: MESSAGES_PAGE_SIZE,
          currentUserId: userId,
        });

        // Drop stale responses if the user switched chats (or cleared) mid-request.
        if (
          String(stateRef.current.activeConversationId ?? '') !==
          String(conversationId)
        ) {
          return;
        }

        if (silent && stateRef.current.messages.length > 0) {
          // Append only brand-new messages — no full list reload / flicker.
          // Never surface AI auto-replies inside human/group MessageContext chats.
          const active = stateRef.current.conversations.find(
            c => String(c.id) === String(conversationId),
          );
          const incoming =
            active && isAiConversationType(active.conversationType)
              ? result.messages
              : excludeAiGeneratedMessages(result.messages, aiAgentUserId);
          dispatch({
            type: MESSAGE_ACTIONS.MERGE_NEW_MESSAGES,
            messages: incoming,
          });
        } else {
          const active = stateRef.current.conversations.find(
            c => String(c.id) === String(conversationId),
          );
          const nextMessages =
            active && isAiConversationType(active.conversationType)
              ? result.messages
              : excludeAiGeneratedMessages(result.messages, aiAgentUserId);
          dispatch({
            type: MESSAGE_ACTIONS.SET_MESSAGES,
            messages: nextMessages,
            page: 1,
            hasNextPage: result.hasNextPage,
          });
        }

        if (result.participants.length) {
          const existing = stateRef.current.conversations.find(
            c => String(c.id) === String(conversationId),
          );
          const isDirect =
            existing?.category !== 'groups' &&
            !String(existing?.conversationType || '')
              .toLowerCase()
              .includes('group');
          const agentId =
            aiAgentUserId != null ? String(aiAgentUserId) : null;
          const peer = isDirect
            ? result.participants.find(
                p =>
                  (userId == null || String(p.id) !== String(userId)) &&
                  (!agentId || String(p.id) !== agentId),
              )
            : undefined;
          const peerHandle = peer?.username?.trim() || peer?.name?.trim();
          await updateConversation(conversationId, {
            members: result.participants,
            memberCount: result.participants.length,
            ...(peer
              ? {
                  ...(peerHandle
                    ? {
                        username: peerHandle,
                        userName: peerHandle,
                        name: peerHandle,
                      }
                    : {}),
                  avatar: peer.avatar || existing?.avatar || null,
                  peerUserId: peer.id,
                  peerUserGuid: peer.profileUserGuid ?? null,
                }
              : {}),
          });
        }
        if (!silent) {
          // Previously marked read here. Moved to UI mount.
        } else if (
          String(stateRef.current.activeConversationId) === String(conversationId)
        ) {
          // Prevent read spam.
        }
      } catch (error) {
        if (
          String(stateRef.current.activeConversationId ?? '') !==
          String(conversationId)
        ) {
          return;
        }
        if (!silent) {
          dispatch({
            type: MESSAGE_ACTIONS.MESSAGES_ERROR,
            error: getUserFacingErrorMessage(
              error,
              i18n.t('messages.errors.loadMessagesFailed'),
            ),
          });
        }
      } finally {
        messageRequestsRef.current.delete(requestKey);
      }
    },
    [userId, updateConversation, aiAgentUserId, stripAiAgentIfPresent],
  );

  /** Lightweight sync for an open chat — merges new messages only. */
  const syncInFlightRef = useRef(false);
  const syncActiveMessages = useCallback(
    async (conversationId?: string | null) => {
      const id =
        conversationId || stateRef.current.activeConversationId;
      if (!id) return;
      if (String(stateRef.current.activeConversationId) !== String(id)) return;
      if (syncInFlightRef.current) return;
      syncInFlightRef.current = true;
      try {
        await fetchMessages(id, { refresh: true, silent: true });
      } finally {
        syncInFlightRef.current = false;
      }
    },
    [fetchMessages],
  );

  const loadMoreMessages = useCallback(
    async (conversationId?: string) => {
      const id = conversationId || stateRef.current.activeConversationId;
      const current = stateRef.current;
      if (
        !id ||
        current.messagesLoadingMore ||
        !current.messagesHasNextPage ||
        current.messagesLoading
      ) {
        return;
      }
      dispatch({ type: MESSAGE_ACTIONS.MESSAGES_LOADING_MORE });
      try {
        const nextPage = current.messagesPage + 1;
        const result = await messageService.getMessages({
          conversationId: id,
          page: nextPage,
          pageSize: MESSAGES_PAGE_SIZE,
          currentUserId: userId,
        });
        if (String(stateRef.current.activeConversationId) !== String(id)) {
          return;
        }
        dispatch({
          type: MESSAGE_ACTIONS.APPEND_MESSAGES,
          messages: excludeAiGeneratedMessages(result.messages, aiAgentUserId),
          page: nextPage,
          hasNextPage: result.hasNextPage,
        });
      } catch (error) {
        dispatch({
          type: MESSAGE_ACTIONS.MESSAGES_ERROR,
          error: getUserFacingErrorMessage(
            error,
            i18n.t('messages.errors.loadMessagesFailed'),
          ),
        });
      }
    },
    [userId, aiAgentUserId],
  );

  const setReplyTo = useCallback((message: ExtendedChatMessage | null) => {
    dispatch({ type: MESSAGE_ACTIONS.SET_REPLY, replyTo: message });
  }, []);

  const clearModerationHold = useCallback(() => {
    dispatch({
      type: MESSAGE_ACTIONS.SET_MODERATION_HOLD,
      moderationHold: null,
    });
  }, []);

  const sendMessage = useCallback(
    async (
      conversationId: string,
      input: SendConversationMessageInput,
    ): Promise<SendMessageResult> => {
      if (!conversationId) return SEND_FAILED;

      // Hard guard: human Chat / GroupChat must never post into AI agent threads
      // (backend auto-generates AI replies on SendMessage for agent conversations).
      if (robiConversationId && String(conversationId) === robiConversationId) {
        console.warn(
          '[MessageContext] Blocked sendMessage to Robi conversation from standard messaging path',
          conversationId,
        );
        return SEND_FAILED;
      }

      const active =
        stateRef.current.conversations.find(
          c => String(c.id) === String(conversationId),
        ) ?? null;
      if (
        active &&
        (active.category === 'ai' ||
          isAiConversationType(active.conversationType))
      ) {
        console.warn(
          '[MessageContext] Blocked sendMessage to AI conversation from standard messaging path',
          conversationId,
        );
        return SEND_FAILED;
      }

      if (sendingInFlightRef.current) return SEND_FAILED;
      sendingInFlightRef.current = true;
      dispatch({ type: MESSAGE_ACTIONS.SENDING, sending: true });
      // A previous hold must not outlive the next attempt, or a successful
      // retry would still show the guideline guidance.
      dispatch({
        type: MESSAGE_ACTIONS.SET_MODERATION_HOLD,
        moderationHold: null,
      });
      try {
        // Strip any followed AI agent from this human/group thread before send.
        // Backend generates AI replies whenever an agent is still a participant.
        await stripAiAgentIfPresent(conversationId);

        const replyId =
          input.replyToMessageId ??
          (stateRef.current.replyTo?._id != null
            ? Number(stateRef.current.replyTo._id)
            : null);

        const sent = await messageService.sendMessage({
          conversationId,
          currentUserId: userId,
          input: {
            text: input.text,
            replyToMessageId:
              replyId != null && Number.isFinite(Number(replyId))
                ? Number(replyId)
                : null,
            imageUrl: input.imageUrl,
            videoUrl: input.videoUrl,
            fileUrl: input.fileUrl,
            fileName: input.fileName,
            fileSizeBytes: input.fileSizeBytes,
          },
        });

        const localReply =
          input.replyTo ||
          (stateRef.current.replyTo
            ? buildReplyToFromMessage(stateRef.current.replyTo)
            : null);
        const backendMessageId = toBackendMessageId(sent._id);
        const preview =
          input.text?.trim() ||
          (input.imageUrl || input.videoUrl || input.fileUrl
            ? i18n.t('chat.attachment')
            : '');
        const messageWithReply: ExtendedChatMessage = {
          ...sent,
          // SendMessage occasionally answers without echoing the row back;
          // keep what the user actually typed so the bubble is not blank.
          text: sent.text || input.text || preview,
          // Preserve the image/video URL when the API doesn't echo the
          // attachment back (common with Giphy CDN URLs in chat).
          image: sent.image || input.imageUrl || undefined,
          video: sent.video || input.videoUrl || undefined,
          replyTo: mergeReplyTo(sent.replyTo, localReply),
          user: {
            ...sent.user,
            _id: userId ?? sent.user?._id,
          },
        };

        dispatch({
          type: MESSAGE_ACTIONS.PREPEND_MESSAGE,
          message: messageWithReply,
        });
        dispatch({ type: MESSAGE_ACTIONS.SET_REPLY, replyTo: null });

        await updateConversation(conversationId, {
          lastMessage: preview,
          lastMessageByMe: true,
          lastMessageAt: Date.now(),
          // Only persist ids the API can address — never a local placeholder.
          ...(backendMessageId != null
            ? { lastMessageId: String(backendMessageId) }
            : {}),
          time: formatContentTimestamp(Date.now(), 'utc'),
          isUnread: false,
          unreadCount: 0,
        });

        // The sender has obviously read what they just sent, but the backend
        // only records receipts it is told about — without this the thread
        // answers "1 unread" to its own author on the next count.
        markConversationRead(conversationId).catch(() => undefined);

        // The echo was unusable, so pull the authoritative row from the server.
        if (backendMessageId == null) {
          syncActiveMessages(conversationId).catch(() => undefined);
        }

        return { message: messageWithReply, heldForReview: false };
      } catch (error) {
        if (isModerationHoldError(error)) {
          // The API saved the row and answered 422. It is withheld from the
          // recipient, but `USP_Message_GetConversationMessages` returns a held
          // row to its own sender, so re-reading the thread surfaces it with an
          // "under review" marker instead of leaving the send looking lost.
          dispatch({
            type: MESSAGE_ACTIONS.SET_MODERATION_HOLD,
            moderationHold: {
              conversationId,
              kind: 'send',
              text: input.text ?? '',
            },
          });
          syncActiveMessages(conversationId).catch(() => undefined);
          return { message: null, heldForReview: true };
        }
        dispatch({
          type: MESSAGE_ACTIONS.MESSAGES_ERROR,
          error: getUserFacingErrorMessage(
            error,
            i18n.t('chat.errors.sendFailed'),
          ),
        });
        return SEND_FAILED;
      } finally {
        sendingInFlightRef.current = false;
        dispatch({ type: MESSAGE_ACTIONS.SENDING, sending: false });
      }
    },
    [
      userId,
      updateConversation,
      markConversationRead,
      stripAiAgentIfPresent,
      robiConversationId,
      syncActiveMessages,
    ],
  );

  const editMessage = useCallback(
    async (
      conversationId: string,
      messageId: string | number,
      text: string,
    ): Promise<EditMessageResult> => {
      dispatch({
        type: MESSAGE_ACTIONS.SET_MODERATION_HOLD,
        moderationHold: null,
      });
      try {
        const updated = await messageService.updateMessage({
          conversationId,
          messageId,
          bodyText: text,
          currentUserId: userId,
        });
        dispatch({
          type: MESSAGE_ACTIONS.REPLACE_MESSAGE,
          messageId,
          message: updated,
        });
        return { heldForReview: false };
      } catch (error) {
        if (isModerationHoldError(error)) {
          // An edit that trips moderation is stored for review but not applied
          // to the visible message, so the thread is re-read to show whichever
          // version the API now considers current.
          dispatch({
            type: MESSAGE_ACTIONS.SET_MODERATION_HOLD,
            moderationHold: { conversationId, kind: 'edit', text },
          });
          syncActiveMessages(conversationId).catch(() => undefined);
          return { heldForReview: true };
        }
        dispatch({
          type: MESSAGE_ACTIONS.MESSAGES_ERROR,
          error: getUserFacingErrorMessage(
            error,
            i18n.t('chat.errors.editFailed'),
          ),
        });
        throw error;
      }
    },
    [userId, syncActiveMessages],
  );

  const deleteMessage = useCallback(
    async (conversationId: string, messageId: string | number) => {
      try {
        await messageService.deleteMessage({ conversationId, messageId });
        dispatch({ type: MESSAGE_ACTIONS.REMOVE_MESSAGE, messageId });
      } catch (error) {
        dispatch({
          type: MESSAGE_ACTIONS.MESSAGES_ERROR,
          error: getUserFacingErrorMessage(
            error,
            i18n.t('chat.errors.deleteFailed'),
          ),
        });
        throw error;
      }
    },
    [],
  );

  const openOrCreateDirectConversation = useCallback(
    async (input: OpenDirectConversationInput): Promise<Conversation> => {
      const peerId = Number(input.userId);
      if (!Number.isFinite(peerId) || peerId <= 0) {
        throw new Error(i18n.t('chat.errors.invalidUserId'));
      }
      if (userId != null && peerId === Number(userId)) {
        throw new Error(i18n.t('chat.errors.invalidUserId'));
      }
      if (aiAgentUserId != null && Number(aiAgentUserId) === peerId) {
        throw new Error(i18n.t('chat.errors.openFailed'));
      }

      const finalizeHumanConversation = async (conversation: Conversation) => {
        if (isAiConversationType(conversation.conversationType)) {
          throw new Error(i18n.t('chat.errors.openFailed'));
        }
        await stripAiAgentIfPresent(conversation.id);
        return conversation;
      };

      // Dedupe needs the inbox in hand: without it a deferred list would send
      // every "Message" tap to Conversation/Initiate for an existing thread.
      await ensureConversationsLoaded();

      const existing = findDirectConversationByUserId(peerId);
      if (existing) {
        return finalizeHumanConversation(existing);
      }

      const resolvedPeerName =
        input.userName?.trim() ||
        input.username?.trim() ||
        input.name?.trim() ||
        null;
      const resolvedPeerAvatar =
        typeof input.avatar === 'string' && input.avatar.trim()
          ? input.avatar.trim()
          : null;

      const seedPrefs: Partial<Conversation> = {
        name: resolvedPeerName ?? undefined,
        username: resolvedPeerName ?? undefined,
        userName: resolvedPeerName ?? undefined,
        avatar: resolvedPeerAvatar ?? undefined,
        peerUserId: String(peerId),
        ...(prefsRef.current[String(peerId)] || {}),
      };

      try {
        const { conversation } = await messageService.initiateDirectConversation(
          {
            participantUserId: peerId,
            conversationName: resolvedPeerName,
            avatarUrl: resolvedPeerAvatar,
          },
          userId,
          seedPrefs,
        );
        const human = await finalizeHumanConversation(conversation);
        const enrichedHuman: Conversation = {
          ...human,
          name:
            human.name && !human.name.startsWith('User ')
              ? human.name
              : resolvedPeerName || human.name,
          username: human.username || resolvedPeerName || null,
          userName: human.userName || resolvedPeerName || null,
          avatar: human.avatar || resolvedPeerAvatar || null,
          peerUserId: human.peerUserId || String(peerId),
          members: human.members?.length
            ? human.members
            : [
                {
                  id: String(peerId),
                  name: resolvedPeerName || `User ${peerId}`,
                  username: resolvedPeerName || null,
                  avatar: resolvedPeerAvatar || null,
                },
              ],
        };
        await updatePrefsFor(enrichedHuman.id, pickPrefs(enrichedHuman));
        dispatch({
          type: MESSAGE_ACTIONS.UPSERT_CONVERSATION,
          conversation: enrichedHuman,
        });
        return enrichedHuman;
      } catch (error) {
        const err = error as Error & { code?: string };
        if (err.code === 'CONVERSATION_EXISTS') {
          await fetchConversations({ refresh: true });
          const matched = findDirectConversationByUserId(peerId);
          if (matched) {
            const updated: Conversation = {
              ...matched,
              name:
                matched.name && !matched.name.startsWith('User ')
                  ? matched.name
                  : resolvedPeerName || matched.name,
              username: matched.username || resolvedPeerName || null,
              userName: matched.userName || resolvedPeerName || null,
              avatar: matched.avatar || resolvedPeerAvatar || null,
              peerUserId: matched.peerUserId || String(peerId),
            };
            if (resolvedPeerName || resolvedPeerAvatar) {
              await updatePrefsFor(updated.id, pickPrefs(updated));
              dispatch({
                type: MESSAGE_ACTIONS.UPSERT_CONVERSATION,
                conversation: updated,
              });
            }
            return finalizeHumanConversation(updated);
          }
        }
        throw error instanceof Error
          ? error
          : new Error(i18n.t('chat.errors.openFailed'));
      }
    },
    [
      findDirectConversationByUserId,
      fetchConversations,
      ensureConversationsLoaded,
      userId,
      aiAgentUserId,
      stripAiAgentIfPresent,
      updatePrefsFor,
    ],
  );

  const createGroupConversation = useCallback(
    async ({
      groupName = '',
      members = [],
      createdBy,
    }: CreateGroupConversationInput): Promise<Conversation> => {
      const participantUserIds = members
        .map(m => Number(m.id))
        .filter(id => Number.isFinite(id) && id > 0);

      if (!participantUserIds.length) {
        throw new Error(i18n.t('createGroupChat.validationNoMembers'));
      }

      const conversation = await messageService.initiateGroupConversation(
        {
          participantUserIds,
          conversationName: groupName.trim() || null,
        },
        userId,
      );

      const resolvedCreatedBy =
        conversation.createdBy ??
        createdBy ??
        (userId != null ? String(userId) : null);

      const upserted: Conversation = {
        ...conversation,
        createdBy: resolvedCreatedBy,
        members:
          conversation.members?.length
            ? conversation.members
            : members,
        memberCount:
          conversation.memberCount ||
          members.length ||
          participantUserIds.length,
      };

      dispatch({
        type: MESSAGE_ACTIONS.UPSERT_CONVERSATION,
        conversation: upserted,
      });
      return upserted;
    },
    [userId],
  );

  const addParticipant = useCallback(
    async (
      conversationId: string,
      participantUserId: number,
      isAdmin = false,
    ) => {
      await messageService.addParticipant({
        conversationId,
        participantUserId,
        isAdmin,
      });
      const participants = await messageService.getParticipants(conversationId);
      await updateConversation(conversationId, {
        members: participants,
        memberCount: participants.length,
      });
    },
    [updateConversation],
  );

  const removeParticipant = useCallback(
    async (conversationId: string, participantUserId: number) => {
      await messageService.removeParticipant({
        conversationId,
        participantUserId,
      });
      const participants = await messageService.getParticipants(conversationId);
      await updateConversation(conversationId, {
        members: participants,
        memberCount: participants.length,
      });
      return participants;
    },
    [updateConversation],
  );

  const searchUsers = useCallback(
    async (searchQuery: string): Promise<ConversationMember[]> => {
      const result = await messageService.searchUsers({
        searchQuery,
        page: 1,
        pageSize: 30,
      });
      return result.users;
    },
    [],
  );

  const fetchMemberCandidates = useCallback(
    async (searchQuery?: string): Promise<ConversationMember[]> => {
      return messageService.getMemberCandidates({
        currentUserId: userId,
        searchTerm: searchQuery,
        prefs: prefsRef.current,
      });
    },
    [userId],
  );

  const fetchFollowingCandidates = useCallback(
    async (searchQuery?: string): Promise<ConversationMember[]> => {
      return messageService.getFollowingCandidates({
        currentUserId: userId,
        searchTerm: searchQuery,
      });
    },
    [userId],
  );

  const fetchFollowerCandidates = useCallback(
    async (searchQuery?: string): Promise<ConversationMember[]> => {
      return messageService.getFollowerCandidates({
        currentUserId: userId,
        searchTerm: searchQuery,
      });
    },
    [userId],
  );

  const fetchBuddyCandidates = useCallback(
    async (searchQuery?: string): Promise<ConversationMember[]> => {
      return messageService.getBuddyCandidates({
        currentUserId: userId,
        searchTerm: searchQuery,
      });
    },
    [userId],
  );

  const getParticipants = useCallback(
    async (conversationId: string): Promise<ConversationMember[]> => {
      return messageService.getParticipants(conversationId);
    },
    [],
  );

  const applyChatNotification = useCallback(
    async (payload: NotificationPayload) => {
      const {
        conversationId: rawConversationId,
        type,
        messagePreview,
        senderName,
        senderId,
        reaction,
      } = payload;

      const conversationId = rawConversationId
        ? String(rawConversationId)
        : null;
      if (!conversationId) return;
      if (type === 'robi_message') return;
      if (
        senderId != null &&
        userId != null &&
        String(senderId) === String(userId)
      ) {
        return;
      }

      const existing = stateRef.current.conversations.find(
        c => String(c.id) === conversationId,
      );
      const now = Date.now();
      const isActive =
        String(stateRef.current.activeConversationId || '') === conversationId;

      if (!existing) {
        await fetchConversations({ refresh: true });
        // Still refresh the open thread even if the list row was missing.
        if (isActive) {
          await fetchMessages(conversationId, { refresh: true, silent: true });
        }
        return;
      }

      let lastMessage = messagePreview || existing.lastMessage;
      if (type === 'chat_reaction') {
        lastMessage = i18n.t('chatNotifications.reactionBody', {
          name: senderName,
          reaction: reaction || '❤️',
        });
      } else if (type === 'chat_reply') {
        lastMessage = i18n.t('chatNotifications.replyBody', {
          name: senderName,
        });
      }

      await updateConversation(conversationId, {
        lastMessage,
        lastMessageAt: now,
        isUnread: !isActive,
        unreadCount: isActive
          ? 0
          : Math.max(1, (existing.unreadCount ?? 0) + 1),
        time: formatContentTimestamp(now, 'utc'),
      });

      if (isActive) {
        await fetchMessages(conversationId, { refresh: true, silent: true });
      }
    },
    [fetchConversations, fetchMessages, updateConversation, userId],
  );

  const sortedConversations = useMemo(
    () => sortConversations(state.conversations),
    [state.conversations],
  );
  const totalUnreadCount = useMemo(
    () => sumUnreadByConversationId(state.unreadByConversationId),
    [state.unreadByConversationId],
  );
  const unreadConversationCount = useMemo(
    () => countUnreadConversations(state.unreadByConversationId),
    [state.unreadByConversationId],
  );

  const value = useMemo<MessageContextValue>(
    () => ({
      conversations: sortedConversations,
      rawConversations: state.conversations,
      loading: state.loading,
      refreshing: state.refreshing,
      loadingMore: state.loadingMore,
      hydrated: state.hydrated,
      listLoadedOnce: state.listLoadedOnce,
      error: state.error,
      hasNextPage: state.hasNextPage,
      activeConversationId: state.activeConversationId,
      messages: state.messages,
      messagesLoading: state.messagesLoading,
      messagesLoadingMore: state.messagesLoadingMore,
      messagesError: state.messagesError,
      messagesHasNextPage: state.messagesHasNextPage,
      sending: state.sending,
      replyTo: state.replyTo,
      moderationHold: state.moderationHold,
      totalUnreadCount,
      unreadConversationCount,
      fetchConversations,
      ensureConversationsLoaded,
      loadMoreConversations,
      togglePin,
      toggleMute,
      toggleFavourite,
      blockConversation,
      deleteConversation,
      markConversationRead,
      createGroupConversation,
      openOrCreateDirectConversation,
      selectConversation,
      fetchMessages,
      syncActiveMessages,
      loadMoreMessages,
      sendMessage,
      editMessage,
      deleteMessage,
      setReplyTo,
      clearModerationHold,
      addParticipant,
      removeParticipant,
      searchUsers,
      fetchMemberCandidates,
      fetchFollowingCandidates,
      fetchFollowerCandidates,
      fetchBuddyCandidates,
      getParticipants,
      applyChatNotification,
      updateConversation,
      syncConversationDetails,
      getConversationById,
      findDirectConversationByUserId,
    }),
    [
      sortedConversations,
      totalUnreadCount,
      unreadConversationCount,
      state.conversations,
      state.loading,
      state.refreshing,
      state.loadingMore,
      state.hydrated,
      state.listLoadedOnce,
      state.error,
      state.hasNextPage,
      state.activeConversationId,
      state.messages,
      state.messagesLoading,
      state.messagesLoadingMore,
      state.messagesError,
      state.messagesHasNextPage,
      state.sending,
      state.replyTo,
      state.moderationHold,
      fetchConversations,
      ensureConversationsLoaded,
      loadMoreConversations,
      togglePin,
      toggleMute,
      toggleFavourite,
      blockConversation,
      deleteConversation,
      markConversationRead,
      createGroupConversation,
      openOrCreateDirectConversation,
      selectConversation,
      fetchMessages,
      syncActiveMessages,
      loadMoreMessages,
      sendMessage,
      editMessage,
      deleteMessage,
      setReplyTo,
      clearModerationHold,
      addParticipant,
      removeParticipant,
      searchUsers,
      fetchMemberCandidates,
      fetchFollowingCandidates,
      fetchFollowerCandidates,
      fetchBuddyCandidates,
      getParticipants,
      applyChatNotification,
      updateConversation,
      syncConversationDetails,
      getConversationById,
      findDirectConversationByUserId,
    ],
  );

  return (
    <messageContext.Provider value={value}>{children}</messageContext.Provider>
  );
};

export const useMessages = (): MessageContextValue => {
  const messages = useContext(messageContext);
  if (!messages) {
    throw new Error('useMessages must be used within a MessageProvider');
  }
  return messages;
};

export default messageContext;
